import http from '@/api/http'
import { clientBusinessServicesPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface ClientBusinessService {
  // One business service occurrence from GET `/business-services/clients/{client_id}` (cursor-paginated, DESCENDING by date_time).

  business_service_type_id: string
  // Reference to the parent service type (joined client-side against `/business-service-types`).

  date_time: string
  // ISO 8601 datetime at which this service took place.

  base_price: number
  // Full price before any discount, in major currency units. Shown
  // alongside `price_with_discount` when they differ; not itself used for
  // the balance/Δ calculation.

  price_with_discount: number
  // Price actually charged for this occurrence in major currency units
  // (after any applicable discount). This is the value the client's
  // running balance is computed from.

  subscription_id?: string | null
  // Subscription that covered this visit; null/absent when it was paid for.
}

export interface ClientBusinessServicesResponse extends PageMeta {
  // Paginated list payload from GET `/business-services/clients/{client_id}`.

  items: ClientBusinessService[]
  // Page of business service occurrences for one client.
}

/**
 * Fetches a page of business services attended by a single client. Same
 * cursor/limit contract as the other paginated list fetchers.
 *
 * @param clientId - Person id of the client.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchClientBusinessServices(
  clientId: string,
  cursor?: number,
): Promise<ClientBusinessServicesResponse> {
  const { data } = await http.get<ClientBusinessServicesResponse>(
    `/business-services/clients/${clientId}`,
    { params: cursorParams(clientBusinessServicesPageLimit, cursor) },
  )
  if (import.meta.env.DEV) {
    console.log(
      `[fetchClientBusinessServices] GET /business-services/clients/${clientId} response`,
      data,
    )
  }
  return data
}
